import {
  Alert,
  Box,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import '@amzn/awsui-global-styles/polaris.css';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from './apolloClient';
import Amplify, { Auth } from 'aws-amplify';
import { createBrowserHistory } from 'history';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Router } from 'react-router';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { AuditHistory } from './../src/AuditHistory/AuditHistory';
import { Juno } from './../src/Juno/Juno';
import { LSETool } from './../src/LSETool/LSETool';
import { OpsTransparency } from './../src/OpsTransparency/OpsTransparency';
import { amplify, oauth } from './amplify';
import reportWebVitals from './reportWebVitals';
import { TumblerHome } from './TumblerHome';
import { AppContext, getInitialAppContext } from './utils/appContext';
import { Flyout } from './utils/FlyoutContext';
import { ComponentReleaseTracker } from './SDPDSNav/Components/ComponentReleaseTracker';
import { SoftwareUpdateTracker } from './SDPDSNav/Components/SoftwareUpdateTracker';
import { Integration } from './Integration/Integration';
import { getPiezoClient } from './client/piezo';
import { isPiezoEnabledEnvironment } from './client/piezo/signing/environment';
import { PiezoDebugPrefix } from './client/piezo/provisioning/PiezoClient.constants';
import { getSharedPiezoWasmModule } from './client/piezo/wasm/sharedModule';
import { getPiezoWorkerPool } from './client/piezo/pool/workerPool';

const appContext = getInitialAppContext();

// Amplify.Logger.LOG_LEVEL = IS_DEVELOPMENT_ENV ? 'DEBUG' : undefined;

// ADC regions use Midway auth at APIGW layer — no Cognito/Amplify needed
if (
  !appContext.region.partition ||
  (!appContext.region.partition.includes('iso') && !appContext.region.partition.includes('eusc'))
) {
  Amplify.configure(amplify(appContext));
  Auth.configure({ oauth: oauth(appContext) });
}

function App() {
  let history = createBrowserHistory();
  const [loading, setLoading] = useState(true);
  const updateEuiTheme = async () => {
    if (localStorage.getItem('darkMode') === 'true') {
      await import('@elastic/eui/dist/eui_theme_dark.css');
    } else {
      await import('@elastic/eui/dist/eui_theme_light.css');
    }
  }
  useEffect(() => {
    // ADC regions use Midway auth at APIGW layer — no Cognito
    if (
      appContext.region.partition &&
      (appContext.region.partition.includes('iso') || appContext.region.partition.includes('eusc'))
    ) {
      updateEuiTheme();
      setLoading(false);
      return;
    }
    // Starts the WASM fetch + compile (the longest single Piezo startup step, ~20s for 4.6 MB) ahead of
    // the Cognito round trip below. Memoised and never rejects.
    if (isPiezoEnabledEnvironment()) {
      void getSharedPiezoWasmModule();
    }
    Auth.currentAuthenticatedUser()
      .then((user) => {
        if (!user) {
          Auth.federatedSignIn({ customProvider: 'Amazon' });
        }
        // Deliberately not awaited: the page must render without waiting on a cold init.
        if (user && isPiezoEnabledEnvironment()) {
          const piezoInit = getPiezoClient().init({ region: appContext.region.publicRegion });
          piezoInit
            .then((piezoResult) => {
              if (piezoResult.error) {
                console.warn(`${PiezoDebugPrefix} init skipped/failed: ${piezoResult.error.message}`);
              } else {
                console.debug(`${PiezoDebugPrefix} init complete on operator entry`);
              }
            })
            .catch((piezoErr) => console.warn(`${PiezoDebugPrefix} init threw: ${String(piezoErr)}`));

          // Warm the worker pool after init populates IndexedDB (ordering gate: workers hydrate
          // read-only from the cache the main thread just wrote). Best-effort: warm-up failure leaves
          // signing on the main thread.
          const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
          getPiezoWorkerPool()
            .warmUp(piezoInit, appContext.region.publicRegion, cores)
            .catch((err) =>
              console.warn(`${PiezoDebugPrefix} worker pool warm-up failed: ${String(err)}`),
            );
        }
        if (sessionStorage.getItem('url')) {
          let url = sessionStorage.getItem('url');
          sessionStorage.removeItem('url');
          history.push(url);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (!sessionStorage.getItem('url')) {
          sessionStorage.setItem(
            'url',
            window.location.href.split(window.location.host)[1]
          );
        }
        Auth.federatedSignIn({ customProvider: 'Amazon' });
      });
      updateEuiTheme()
  }, []);

  return (
    <div className="awsui">
      {loading ? (
        <Box margin={{ top: 'l' }} textAlign="center">
          <SpaceBetween size="l" direction="vertical">
            <Alert visible={loading} type="info">
              Cognito Authentication in process. Please wait.
            </Alert>
            <Spinner size="large" />
          </SpaceBetween>
        </Box>
      ) : (
        <ApolloProvider client={apolloClient}>
          <AppContext.Provider value={appContext}>
            <Flyout>
              <Router history={history}>
                <Switch>
                  <Route path="/ops-transparency" component={OpsTransparency} />
                  <Route path="/lse-tool" component={LSETool} />
                  <Route path="/juno" component={Juno} />
                  <Route path="/audit-history" component={AuditHistory} />
                  <Route path="/integration" component={Integration} />
                  <Route path="/component-release-tracker" component={ComponentReleaseTracker} />
                  <Route path="/software-update-tracker" component={SoftwareUpdateTracker} />
                  <Route component={TumblerHome} />
                </Switch>
              </Router>
            </Flyout>
          </AppContext.Provider>
        </ApolloProvider>
      )}
    </div>
  );
}

const AppWrapper = () => {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
};

ReactDOM.render(<AppWrapper />, document.getElementById('root'));
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
